import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileText, Receipt, Edit3, ClipboardList, Search, ChevronRight, User as UserIcon, Eye, Download, X } from "lucide-react";
import { Button, Card, Badge, Spinner, Input } from "../shared/ui";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-AU') : '-';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-AU') : '-';

const SECTIONS = [
  { key: 'payslips',                   label: 'Payslips',                 icon: Receipt },
  { key: 'document_submissions',       label: 'Document Submissions',     icon: FileText },
  { key: 'form_submissions',           label: 'Form Submissions',         icon: ClipboardList },
  { key: 'signed_acknowledgements',    label: 'Signed Acknowledgements',  icon: Edit3 }
];

const EmployeeRecordsPage = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const userId = params.get('user_id');

  const [roster, setRoster] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [section, setSection] = useState('payslips');
  const [previewUrl, setPreviewUrl] = useState(null);

  const isAuthorized = ['admin', 'manager', 'office_staff'].includes(user?.role);

  const fetchRoster = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/employee-records`);
      setRoster(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchDetail = async (uid) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/employee-records?user_id=${uid}`);
      setDetail(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!isAuthorized) { setLoading(false); return; }
    if (userId) fetchDetail(userId);
    else { setDetail(null); fetchRoster(); }
    // eslint-disable-next-line
  }, [userId, isAuthorized]);

  if (!isAuthorized) {
    return (
      <Card className="text-center py-16">
        <p className="font-bold uppercase text-red-600">Restricted</p>
        <p className="text-sm text-slate-500 mt-1">Admin, manager or office_staff only.</p>
      </Card>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  // ─────────── Detail view ───────────
  if (userId && detail) {
    const e = detail.employee;
    const c = detail.counts;
    const items = detail[section] || [];

    return (
      <div className="space-y-4" data-testid="employee-records-detail">
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setParams({}); }}
            data-testid="back-to-roster"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> All Employees
          </Button>
          <div className="flex-1 min-w-[200px]">
            <h1 className="text-2xl font-bold uppercase">{e.first_name} {e.last_name}</h1>
            <p className="text-slate-500 text-sm">{e.role} · {e.email}</p>
          </div>
          <Badge variant="info">{c.total} records</Badge>
        </div>

        {/* Section tabs */}
        <div className="flex gap-1 border-b-2 border-slate-200 overflow-x-auto">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            const active = section === s.key;
            const count = c[s.key] || 0;
            return (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                data-testid={`section-${s.key}`}
                className={`px-3 py-2 font-bold text-xs uppercase -mb-[2px] border-b-2 flex items-center gap-2 whitespace-nowrap ${
                  active ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {s.label}
                <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-[10px] text-slate-700">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Section body */}
        {items.length === 0 ? (
          <Card className="text-center py-10 text-slate-500 text-sm">No {SECTIONS.find(x => x.key === section).label.toLowerCase()} for this employee yet</Card>
        ) : section === 'payslips' ? (
          <PayslipsTable items={items} onPreview={setPreviewUrl} />
        ) : section === 'document_submissions' ? (
          <DocSubsTable items={items} onPreview={setPreviewUrl} />
        ) : section === 'form_submissions' ? (
          <FormSubsTable items={items} />
        ) : (
          <AcknowledgementsTable items={items} onPreview={setPreviewUrl} />
        )}

        {/* Preview modal */}
        {previewUrl && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)}>
            <div className="bg-white rounded-lg p-4 max-w-3xl max-h-[90vh] overflow-auto" onClick={(ev) => ev.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <p className="font-bold text-sm">{previewUrl.caption || 'Preview'}</p>
                <button onClick={() => setPreviewUrl(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
              </div>
              {previewUrl.url?.toLowerCase().endsWith('.pdf') ? (
                <iframe src={previewUrl.url} title="Document" className="w-full h-[75vh] border rounded" />
              ) : (
                <img src={previewUrl.url} alt="" className="w-full max-h-[75vh] object-contain rounded border" />
              )}
              <div className="mt-3 flex justify-end">
                <a href={previewUrl.url} download target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase bg-slate-100 hover:bg-slate-200 rounded">
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─────────── Roster view ───────────
  const employees = roster?.employees || [];
  const filtered = search
    ? employees.filter(e => `${e.first_name} ${e.last_name} ${e.email} ${e.role}`.toLowerCase().includes(search.toLowerCase()))
    : employees;

  return (
    <div className="space-y-4" data-testid="employee-records-roster">
      <div>
        <h1 className="text-2xl font-bold uppercase">Employee Records</h1>
        <p className="text-slate-500 mt-1 text-sm">All payslips, document uploads, form submissions and acknowledgement signatures, categorised by employee.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="text-center p-3">
          <p className="text-2xl font-bold">{roster?.total_employees || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Employees</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-blue-600">{employees.reduce((a, e) => a + e.counts.payslips, 0)}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Payslips</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-amber-600">{employees.reduce((a, e) => a + e.counts.document_submissions, 0)}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Document Uploads</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-green-600">{employees.reduce((a, e) => a + e.counts.form_submissions + e.counts.signed_acknowledgements, 0)}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Forms Filled / Signed</p>
        </Card>
      </div>

      <Card className="bg-slate-50 p-3">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search by name, email or role…"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
            testId="employee-records-search"
          />
        </div>
      </Card>

      <div className="overflow-x-auto">
        <table className="w-full text-sm bg-white rounded-lg border">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-3 font-bold">Employee</th>
              <th className="text-left p-3 font-bold">Role</th>
              <th className="text-right p-3 font-bold">Payslips</th>
              <th className="text-right p-3 font-bold">Docs</th>
              <th className="text-right p-3 font-bold">Forms</th>
              <th className="text-right p-3 font-bold">Acks</th>
              <th className="text-left p-3 font-bold">Latest activity</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-orange-50 cursor-pointer" onClick={() => setParams({ user_id: e.id })} data-testid={`emp-row-${e.id}`}>
                <td className="p-3 font-semibold flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-xs font-bold text-orange-700 flex-shrink-0">
                    {e.first_name?.[0]}{e.last_name?.[0]}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate">{e.first_name} {e.last_name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{e.email}</div>
                  </div>
                </td>
                <td className="p-3 text-xs uppercase">{e.role}</td>
                <td className="p-3 text-right">{e.counts.payslips || <span className="text-slate-300">0</span>}</td>
                <td className="p-3 text-right">{e.counts.document_submissions || <span className="text-slate-300">0</span>}</td>
                <td className="p-3 text-right">{e.counts.form_submissions || <span className="text-slate-300">0</span>}</td>
                <td className="p-3 text-right">{e.counts.signed_acknowledgements || <span className="text-slate-300">0</span>}</td>
                <td className="p-3 text-xs text-slate-500">{e.latest_activity ? fmtDateTime(e.latest_activity) : <span className="text-slate-300">—</span>}</td>
                <td className="p-3"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="8" className="text-center text-slate-500 py-8">No employees match your search</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PayslipsTable = ({ items, onPreview }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm bg-white rounded-lg border">
      <thead className="bg-slate-50"><tr>
        <th className="text-left p-2 font-bold">Pay Period</th>
        <th className="text-right p-2 font-bold">Amount</th>
        <th className="text-left p-2 font-bold">Uploaded</th>
        <th className="p-2"></th>
      </tr></thead>
      <tbody>
        {items.map(p => (
          <tr key={p.id} className="border-t border-slate-100">
            <td className="p-2">{fmtDate(p.pay_period_start)} → {fmtDate(p.pay_period_end)}</td>
            <td className="p-2 text-right font-bold text-green-600">{p.gross_amount ? `$${Number(p.gross_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}</td>
            <td className="p-2 text-xs">{fmtDateTime(p.uploaded_at || p.created_at)}</td>
            <td className="p-2">
              {p.file_url && (
                <Button size="sm" variant="ghost" onClick={() => onPreview({ url: p.file_url, caption: `Payslip ${fmtDate(p.pay_period_end)}` })}>
                  <Eye className="w-3.5 h-3.5" />
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DocSubsTable = ({ items, onPreview }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm bg-white rounded-lg border">
      <thead className="bg-slate-50"><tr>
        <th className="text-left p-2 font-bold">Document</th>
        <th className="text-left p-2 font-bold">Category</th>
        <th className="text-left p-2 font-bold">Submitted</th>
        <th className="text-left p-2 font-bold">Status</th>
        <th className="p-2"></th>
      </tr></thead>
      <tbody>
        {items.map(d => (
          <tr key={d.id} className="border-t border-slate-100">
            <td className="p-2 font-semibold">{d.document_title || '—'}</td>
            <td className="p-2 text-xs uppercase">{d.document_category || '-'}</td>
            <td className="p-2 text-xs">{fmtDateTime(d.submitted_at)}</td>
            <td className="p-2">
              <Badge variant={d.status === 'approved' ? 'success' : d.status === 'rejected' ? 'error' : 'warning'}>
                {d.status || 'pending'}
              </Badge>
            </td>
            <td className="p-2">
              {d.file_url && (
                <Button size="sm" variant="ghost" onClick={() => onPreview({ url: d.file_url, caption: d.document_title })}>
                  <Eye className="w-3.5 h-3.5" />
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const FormSubsTable = ({ items }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm bg-white rounded-lg border">
      <thead className="bg-slate-50"><tr>
        <th className="text-left p-2 font-bold">Form</th>
        <th className="text-left p-2 font-bold">Category</th>
        <th className="text-left p-2 font-bold">Job</th>
        <th className="text-left p-2 font-bold">Submitted</th>
        <th className="text-left p-2 font-bold">Approval</th>
      </tr></thead>
      <tbody>
        {items.map(f => (
          <tr key={f.id} className="border-t border-slate-100">
            <td className="p-2 font-semibold">{f.template_name}</td>
            <td className="p-2 text-xs uppercase">{f.template_category || '-'}</td>
            <td className="p-2 text-xs">{f.job_title || <span className="text-slate-400">—</span>}</td>
            <td className="p-2 text-xs">{fmtDateTime(f.submitted_at)}</td>
            <td className="p-2">
              <Badge variant={f.approval_status === 'approved' ? 'success' : f.approval_status === 'pending' ? 'warning' : 'default'}>
                {f.approval_status || 'n/a'}
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AcknowledgementsTable = ({ items, onPreview }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm bg-white rounded-lg border">
      <thead className="bg-slate-50"><tr>
        <th className="text-left p-2 font-bold">Form</th>
        <th className="text-left p-2 font-bold">Category</th>
        <th className="text-left p-2 font-bold">Job</th>
        <th className="text-left p-2 font-bold">Signed</th>
        <th className="p-2"></th>
      </tr></thead>
      <tbody>
        {items.map(s => (
          <tr key={s.id} className="border-t border-slate-100">
            <td className="p-2 font-semibold">{s.template_name}</td>
            <td className="p-2 text-xs uppercase">{s.template_category || '-'}</td>
            <td className="p-2 text-xs">{s.job_title || <span className="text-slate-400">—</span>}</td>
            <td className="p-2 text-xs">{fmtDateTime(s.signed_at)}</td>
            <td className="p-2">
              {s.signature_data && (
                <Button size="sm" variant="ghost" onClick={() => onPreview({ url: s.signature_data, caption: `Signature — ${s.template_name}` })}>
                  <Eye className="w-3.5 h-3.5" />
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default EmployeeRecordsPage;
